import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { reportsService } from '@/services/reports';
import { useAuth } from '@/contexts/auth';
import { useData } from '@/contexts/data';
import { useToast } from '@/hooks/use-toast';
import { getErrorSupportMessage, isAppError } from '@/lib/errors';
import {
  canSendReport,
  canStartReportSend,
  normalizeReportStatus,
  REPORT_STATUS_AWAITING_SEND,
} from '@/lib/reportStatus';
import { Report } from '@/pages/reports/schemas';
import { EMPTY_MESSAGE_TOAST, EMPTY_SELECTION_TOAST } from '../constants';
import type { SendWizardStep } from '../types';
import { htmlToPlainText } from '../utils';
import { useRecipientsList } from './useRecipientsList';
import { useSendMessage } from './useSendMessage';

export function useSendReport() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { partners } = useData();
  const { toast } = useToast();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [step, setStep] = useState<SendWizardStep>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recipients = useRecipientsList(id);
  const message = useSendMessage(report?.edition);
  const previousSelectedCount = useRef(recipients.selectedCount);

  useEffect(() => {
    if (authLoading) return;
    const loadReport = async () => {
      if (!id) {
        setError('ID do report não fornecido');
        setLoading(false);
        return;
      }

      try {
        const reportData = await reportsService.getReportById(id, user?.email);
        if (!reportData) {
          setError('Report não encontrado');
          return;
        }
        const status = normalizeReportStatus(reportData.status);
        if (!canSendReport(status)) {
          setError(
            'Este report ainda está em rascunho. Finalize o report antes de enviar.'
          );
          setReport(reportData);
          return;
        }

        setReport(reportData);
      } catch (error) {
        if (isAppError(error) && error.status === 403) {
          setAccessDenied(true);
        } else {
          setError('Erro ao carregar report');
        }
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [id, user, authLoading]);

  useEffect(() => {
    if (previousSelectedCount.current > 0 && recipients.selectedCount === 0) {
      toast({
        variant: 'warning',
        title: EMPTY_SELECTION_TOAST.title,
        description: EMPTY_SELECTION_TOAST.description,
      });
    }
    previousSelectedCount.current = recipients.selectedCount;
  }, [recipients.selectedCount, toast]);

  const partnerLabel = useMemo(() => {
    if (report?.partner_name?.trim()) return report.partner_name.trim();
    if (report?.partner_id) {
      const partner = partners.find((item) => item.id === report.partner_id);
      if (partner?.name) return partner.name;
    }
    return 'Geral';
  }, [partners, report?.partner_id, report?.partner_name]);

  const reportDate = report
    ? format(report.updatedAt, 'dd/MM/yyyy', { locale: ptBR })
    : '';

  const canContinue =
    step === 1
      ? recipients.selectedCount > 0
      : step === 2
        ? message.isValid
        : recipients.selectedCount > 0 && message.isValid;

  const handleBack = () => {
    if (step > 1) {
      setStep((current) => (current === 3 ? 2 : 1));
      return;
    }
    if (id) navigate(`/app/reports/${id}/view`);
    else navigate('/app/reports');
  };

  const handleCancel = () => {
    if (id) navigate(`/app/reports/${id}/view`);
    else navigate('/app/reports');
  };

  const handleSend = useCallback(async () => {
    if (!id || !report) return;
    const userIds = recipients.sendableSelectedIds;
    const body = htmlToPlainText(message.body);
    if (userIds.length === 0 || !message.subject.trim() || !body) {
      toast({
        variant: 'warning',
        title: EMPTY_SELECTION_TOAST.title,
        description: EMPTY_SELECTION_TOAST.description,
      });
      return;
    }

    const status = normalizeReportStatus(report.status);
    if (!canSendReport(status)) {
      toast({
        variant: 'error',
        title: 'Não foi possível enviar',
        description:
          'Este report ainda está em rascunho. Finalize o report antes de enviar.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (canStartReportSend(status)) {
        const updated = await reportsService.update(id, {
          status: REPORT_STATUS_AWAITING_SEND,
        });
        const nextStatus = normalizeReportStatus(updated.status);
        if (!canSendReport(nextStatus)) {
          throw new Error(
            'Este report ainda está em rascunho. Finalize o report antes de enviar.'
          );
        }
        setReport((current) =>
          current
            ? { ...current, status: nextStatus }
            : current
        );
      }

      await reportsService.sendReport(id, {
        title: message.subject.trim(),
        body,
        user_ids: userIds,
      });
      toast({
        title: 'Envio iniciado',
        description: 'Os e-mails serão disparados em breve.',
      });
      navigate('/app/reports');
    } catch (sendError) {
      toast({
        variant: 'error',
        title: 'Não foi possível enviar',
        description: getErrorSupportMessage(sendError),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    id,
    message.body,
    message.subject,
    navigate,
    recipients.sendableSelectedIds,
    report,
    toast,
  ]);

  const handleContinue = useCallback(() => {
    if (step === 1 && recipients.selectedCount === 0) {
      toast({
        variant: 'warning',
        title: EMPTY_SELECTION_TOAST.title,
        description: EMPTY_SELECTION_TOAST.description,
      });
      return;
    }

    if (step === 1) {
      setStep(2);
      return;
    }

    if (step === 2) {
      if (!message.validate()) {
        toast({
          variant: 'warning',
          title: EMPTY_MESSAGE_TOAST.title,
          description: EMPTY_MESSAGE_TOAST.description,
        });
        return;
      }
      setStep(3);
      return;
    }

    void handleSend();
  }, [handleSend, message, recipients.selectedCount, step, toast]);

  return {
    id,
    report,
    loading,
    error,
    accessDenied,
    step,
    partnerLabel,
    reportDate,
    canContinue,
    isSubmitting,
    handleBack,
    handleCancel,
    handleContinue,
    recipients,
    message,
  };
}
